import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useList, useNavigation, useApiUrl } from '@refinedev/core';
import { formatDate } from '@/lib/utils';
import { adminFetch, AdminApiError } from '@/providers/admin-api';
import { toast } from '@/hooks/useToast';
import { SortableHeader, nextSort, type SortOrder } from '@/components/table/SortableHeader';
import {
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  ExternalLink,
  X,
} from 'lucide-react';

interface Report {
  id: string;
  reporter_id: string;
  reporter_name: string;
  reporter_email: string;
  reported_user_id: string;
  reported_user_name: string;
  reported_user_email: string;
  reason: string;
  description: string | null;
  status: 'pending' | 'reviewed' | 'dismissed' | 'actioned';
  reviewed_by_admin: string | null;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
}

const statusConfig = {
  pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
  reviewed: { color: 'bg-blue-100 text-blue-800', icon: Eye, label: 'Reviewed' },
  dismissed: { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'Dismissed' },
  actioned: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Actioned' },
};

const reasonLabels: Record<string, string> = {
  inappropriate_content: 'Inappropriate Content',
  harassment: 'Harassment',
  spam: 'Spam',
  fake_profile: 'Fake Profile',
  underage: 'Underage User',
  other: 'Other',
};

export function ReportsPage() {
  const { show } = useNavigation();
  const apiUrl = useApiUrl();
  const [searchParams, setSearchParams] = useSearchParams();
  // user-360 clickthrough: /reports?reported_user_id=<id> filters the queue
  // to a single reported user. When present we default the status tab to
  // "all" so every report about them shows, not just pending.
  const reportedUserId = searchParams.get('reported_user_id');
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>(reportedUserId ? 'all' : 'pending');
  const [reasonFilter, setReasonFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const pageSize = 20;

  const handleSort = (field: string) => {
    const next = nextSort({ field: sortField, order: sortOrder }, field);
    setSortField(next.field);
    setSortOrder(next.order);
    setCurrentPage(1);
  };

  const { data, isLoading, isError, refetch } = useList<Report>({
    resource: 'reports',
    pagination: {
      current: currentPage,
      pageSize,
    },
    filters: [
      ...(statusFilter !== 'all' ? [{ field: 'status', operator: 'eq' as const, value: statusFilter }] : []),
      ...(reasonFilter !== 'all' ? [{ field: 'category', operator: 'eq' as const, value: reasonFilter }] : []),
      ...(reportedUserId ? [{ field: 'reported_user_id', operator: 'eq' as const, value: reportedUserId }] : []),
      ...(startDate ? [{ field: 'created_at_gte', operator: 'eq' as const, value: startDate }] : []),
      ...(endDate ? [{ field: 'created_at_lte', operator: 'eq' as const, value: `${endDate}T23:59:59` }] : []),
    ],
    sorters: sortField ? [{ field: sortField, order: sortOrder }] : [{ field: 'created_at', order: 'desc' }],
  });

  const reports = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  // Persist an admin decision to the (already-complete, audited) backend:
  //   PUT /v1/admin/reports/:id  { status, admin_notes }
  // 'dismiss' → dismissed, 'action' → actioned. Returns true on success so
  // callers (e.g. "Take Action on User") can chain a navigation.
  const handleReview = async (
    reportId: string,
    action: 'dismiss' | 'action',
    notes: string,
  ): Promise<boolean> => {
    const status = action === 'dismiss' ? 'dismissed' : 'actioned';
    setSubmitting(true);
    try {
      await adminFetch(`${apiUrl}/v1/admin/reports/${reportId}`, {
        method: 'PUT',
        body: JSON.stringify({ status, admin_notes: notes.trim() || undefined }),
      });
      toast({ title: 'Report updated', description: `Marked as ${status}.` });
      setSelectedReport(null);
      setReviewNotes('');
      await refetch();
      return true;
    } catch (error) {
      toast({
        title: 'Update failed',
        description:
          error instanceof AdminApiError ? error.message : 'Could not update the report.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-destructive">
        Failed to load reports. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Reports</h2>
        <p className="text-muted-foreground">
          Review and take action on user reports
        </p>
      </div>

      {/* user-360 filter banner — shown when arriving from a user's page */}
      {reportedUserId && (
        <div className="flex items-center justify-between rounded-lg border border-primary/30 bg-primary/10 px-4 py-2.5 text-sm">
          <span className="text-foreground">
            Filtered to reports against{' '}
            <code className="text-muted-foreground">{reportedUserId.slice(0, 8)}…</code>
          </span>
          <button
            type="button"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('reported_user_id');
              setSearchParams(next, { replace: true });
            }}
            className="rounded border bg-background px-2.5 py-1 text-xs hover:bg-muted"
          >
            Clear filter
          </button>
        </div>
      )}

      {/* Status Filter Tabs */}
      <div className="flex gap-2 border-b">
        {[
          { value: 'pending', label: 'Pending', icon: Clock },
          { value: 'all', label: 'All', icon: AlertTriangle },
          { value: 'actioned', label: 'Actioned', icon: CheckCircle },
          { value: 'dismissed', label: 'Dismissed', icon: XCircle },
        ].map((tab) => (
          <button
            key={tab.value}
            onClick={() => {
              setStatusFilter(tab.value);
              setCurrentPage(1);
            }}
            className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
              statusFilter === tab.value
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:border-muted hover:text-foreground'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Reason + date-range filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">Reason:</label>
          <select
            value={reasonFilter}
            onChange={(e) => {
              setReasonFilter(e.target.value);
              setCurrentPage(1);
            }}
            aria-label="Filter by reason"
            className="h-9 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="all">All reasons</option>
            {Object.entries(reasonLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="reports-from" className="text-sm text-muted-foreground">From:</label>
          <input
            id="reports-from"
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setCurrentPage(1);
            }}
            className="h-9 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <label htmlFor="reports-to" className="text-sm text-muted-foreground">To:</label>
          <input
            id="reports-to"
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setCurrentPage(1);
            }}
            className="h-9 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        {(reasonFilter !== 'all' || startDate || endDate) && (
          <button
            onClick={() => {
              setReasonFilter('all');
              setStartDate('');
              setEndDate('');
              setCurrentPage(1);
            }}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3" />
            Clear
          </button>
        )}
      </div>

      {/* Reports Table */}
      <div className="rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Report</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Reported User</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Reporter</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Reason</th>
                <SortableHeader
                  field="status"
                  label="Status"
                  activeField={sortField}
                  activeOrder={sortOrder}
                  onSort={handleSort}
                />
                <SortableHeader
                  field="created_at"
                  label="Date"
                  activeField={sortField}
                  activeOrder={sortOrder}
                  onSort={handleSort}
                />
                <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                    {statusFilter === 'pending' ? 'No pending reports' : 'No reports found'}
                  </td>
                </tr>
              ) : (
                reports.map((report) => {
                  const status = statusConfig[report.status];
                  const StatusIcon = status.icon;

                  return (
                    <tr key={report.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <div className="font-mono text-xs text-muted-foreground">
                          {report.id.substring(0, 8)}...
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => show('users', report.reported_user_id)}
                          className="group"
                        >
                          <div className="font-medium text-primary group-hover:underline">
                            {report.reported_user_name}
                          </div>
                          <div className="text-xs text-muted-foreground">{report.reported_user_email}</div>
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">{report.reporter_name}</div>
                        <div className="text-xs text-muted-foreground">{report.reporter_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium">
                          {reasonLabels[report.reason] || report.reason}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${status.color}`}>
                          <StatusIcon className="h-3 w-3" />
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {formatDate(report.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setSelectedReport(report)}
                            className="rounded-lg px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10"
                          >
                            {report.status === 'pending' ? 'Review' : 'View'}
                          </button>
                          <button
                            onClick={() => show('users', report.reported_user_id)}
                            className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                            title="View reported user"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-4 py-3">
            <div className="text-sm text-muted-foreground">
              Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, total)} of {total} reports
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                aria-label="Previous page"
                className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                aria-label="Next page"
                className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Review Modal */}
      {selectedReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-2xl rounded-lg bg-card p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {selectedReport.status === 'pending' ? 'Review Report' : 'Report Details'}
              </h3>
              <button
                onClick={() => {
                  setSelectedReport(null);
                  setReviewNotes('');
                }}
                aria-label="Close modal"
                className="text-muted-foreground hover:text-foreground"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-6 space-y-6">
              {/* Report Info */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-lg bg-muted/50 p-4">
                  <div className="text-sm text-muted-foreground">Reported User</div>
                  <div className="mt-1 font-medium">{selectedReport.reported_user_name}</div>
                  <div className="text-sm text-muted-foreground">{selectedReport.reported_user_email}</div>
                </div>
                <div className="rounded-lg bg-muted/50 p-4">
                  <div className="text-sm text-muted-foreground">Reporter</div>
                  <div className="mt-1 font-medium">{selectedReport.reporter_name}</div>
                  <div className="text-sm text-muted-foreground">{selectedReport.reporter_email}</div>
                </div>
              </div>

              <div className="rounded-lg bg-muted/50 p-4">
                <div className="text-sm text-muted-foreground">Reason</div>
                <div className="mt-1 font-medium">
                  {reasonLabels[selectedReport.reason] || selectedReport.reason}
                </div>
                {selectedReport.description && (
                  <div className="mt-2 text-sm">{selectedReport.description}</div>
                )}
              </div>

              {selectedReport.admin_notes && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <div className="text-sm font-medium text-blue-800">Admin Notes</div>
                  <div className="mt-1 text-sm text-blue-700">{selectedReport.admin_notes}</div>
                </div>
              )}

              {/* Review Form */}
              {selectedReport.status === 'pending' && (
                <div className="space-y-4 border-t pt-4">
                  <div>
                    <label className="text-sm font-medium">Notes (optional)</label>
                    <textarea
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      rows={3}
                      className="mt-1 w-full rounded-lg border bg-background p-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                      placeholder="Add notes about this review..."
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setSelectedReport(null);
                        setReviewNotes('');
                      }}
                      className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-muted"
                    >
                      Cancel
                    </button>
                    <button
                      disabled={submitting}
                      onClick={() => handleReview(selectedReport.id, 'dismiss', reviewNotes)}
                      className="flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <XCircle className="h-4 w-4" />
                      Dismiss
                    </button>
                    <button
                      disabled={submitting}
                      onClick={async () => {
                        // Record the decision first (status → actioned), then
                        // navigate to the reported user so the admin can act
                        // (suspend/ban/etc.). Without persisting, the report
                        // would linger 'pending' forever.
                        const reportedUserId = selectedReport.reported_user_id;
                        const ok = await handleReview(selectedReport.id, 'action', reviewNotes);
                        if (ok) show('users', reportedUserId);
                      }}
                      className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Take Action on User
                    </button>
                  </div>
                </div>
              )}

              {/* Close button for non-pending */}
              {selectedReport.status !== 'pending' && (
                <div className="flex justify-end border-t pt-4">
                  <button
                    onClick={() => {
                      setSelectedReport(null);
                      setReviewNotes('');
                    }}
                    className="rounded-lg bg-muted px-4 py-2 text-sm font-medium hover:bg-muted/80"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
